import BlogPostAPI from "@api/BlogPost.api";
import type IBlogPost from "@api/models/IBlog";
import SSBackgroundImage from "@core-components/backgroundImage/BackgroundImage";
import SSBgImageHeadline from "@core-components/backgroundImage/components/BgImageHeadline";
import BlogListItem from "@core-components/blogs/BlogListItem";
import ChipText from "@core-components/chip-text/ChipText";
import Emoji from "@core-components/emoji/Emoji";
import SSPagination from "@core-components/pagination/Pagination";
import SSHeadline from "@core-components/title/SSHeadline";
import SSLoLGradientText from "@core-components/title/components/SSLoLGradientText";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import EPublicRoutes from "@core-types/routes.enum";
import useAppLanguage from "@hooks/language/useAppLanguage";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Flex, SimpleGrid, Stack } from "@mantine/core";
import { useLoaderStore } from "@modules/loader/useLoader.hook";
import SEO from "@modules/seo/SEO";
import type React from "react";
import { useEffect, useMemo } from "react";
import Widgets from "../components/widgets/Widgets";
import useBlogStore from "../stores/Blog.store";

const BlogList: React.FC = () => {
  const blogStore = useBlogStore();
  const loaderStore = useLoaderStore();
  const { t } = useAppTranslation("pages.blog");
  const { locationUrlBuilder } = useAppLanguage();

  const DUMMY_BLOGS: IBlogPost[] = useMemo(
    () =>
      Array(9).fill({
        slug: "",
        title: t("dummy.title"),
        description: t("dummy.description"),
        image: {
          alt: "",
          image_url: "",
        },
      }),
    [t],
  );

  useEffect(() => {
    getBlogs();
  }, []);

  const getBlogs = () => {
    BlogPostAPI.getBlogPosts(
      blogStore.currentPage,
      blogStore.itemsPerPage,
    ).then((response) => blogStore.setPaginatedBlogs(response.data));
  };

  const renderedBlogs = useMemo(
    () =>
      (loaderStore.isLoadingBySkeleton() ? DUMMY_BLOGS : blogStore.blogs).map(
        (blog) => (
          <BlogListItem
            key={blog.id}
            description={blog.shortDescription}
            href={locationUrlBuilder(EPublicRoutes.BLOGS, blog.slug)}
            label={blog.title}
            picture={blog.coverImage}
          />
        ),
      ),
    [blogStore.blogs, loaderStore.isLoadingBySkeleton(), DUMMY_BLOGS],
  );

  const handlePaginate = (page: number) => {
    blogStore.setCurrentPage(page);
  };

  return (
    <>
      <SEO description={t("seo.description")} title={t("seo.title")} />
      <SSBackgroundImage>
        <SSBgImageHeadline
          subTitle={
            <ChipText
              className="mx-4 mt-16 text-center"
              label={<strong>{t("banner.chip")}</strong>}
            />
          }
        >
          <SSWhiteText>{t("banner.titlePrefix")}</SSWhiteText>
          <br />
          <SSLoLGradientText>{t("banner.titleSuffix")}</SSLoLGradientText>
        </SSBgImageHeadline>
      </SSBackgroundImage>
      <Flex className="w-full flex-col md:flex-row" gap={10}>
        <div className="basis-2/3 p-2">
          <SSHeadline
            props={{
              stack: {
                align: "left",
                className: "pb-8",
              },
              title: {
                size: 24,
              },
            }}
          >
            <Emoji.Rocket size={26} />
            &nbsp;
            <SSLoLGradientText>{t("headlinePrefix")} </SSLoLGradientText>
            <SSWhiteText>{t("headlineSuffix")}</SSWhiteText>
          </SSHeadline>
          <Stack className="pb-9" gap={40}>
            <SimpleGrid
              cols={{ base: 1, sm: 2 }}
              spacing={40}
              verticalSpacing={40}
            >
              {renderedBlogs}
            </SimpleGrid>
            <SSPagination
              currentPage={blogStore.currentPage}
              itemsCount={blogStore.totalBlogs}
              itemsPerPage={blogStore.itemsPerPage}
              pageCount={blogStore.pageCount}
              onPaginate={handlePaginate}
            />
          </Stack>
        </div>
        <div className="sticky top-[75px] w-full self-start md:basis-1/3">
          <Widgets />
        </div>
      </Flex>
    </>
  );
};

export default BlogList;
